import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadonlyVec3, vec3 } from "gl-matrix";

import { type RootState } from "app";
import { setViewpoint } from "features/render";

export interface Coordinate {
    id: string;
    point: { position: ReadonlyVec3; guides?: vec3[]; snapType?: string };
    objectId?: number;
    normal?: ReadonlyVec3;
    screenOffset?: { x: number; y: number };
}

const initialState = {
    coordinates: [] as Coordinate[],
    draggingCoordinate: null as null | Coordinate,
    hideCoordinateGizmo: false,
};

type State = typeof initialState;

export const coordinatesSlice = createSlice({
    name: "coordinates",
    initialState: initialState,
    reducers: {
        addCoordinate: (state, action: PayloadAction<Coordinate>) => {
            state.coordinates = state.coordinates.concat([action.payload]);
        },
        replaceCoordinate: (state, action: PayloadAction<{ coordinate: Coordinate; replaceId: string }>) => {
            const idx = state.coordinates.findIndex((l) => l.id === action.payload.replaceId);
            if (idx === -1) {
                return;
            }
            state.coordinates[idx] = action.payload.coordinate;
        },
        setCoordinates: (state, action: PayloadAction<State["coordinates"]>) => {
            state.coordinates = action.payload;
        },
        setCoordinate: (state, action: PayloadAction<{ idx: number; trace: Coordinate }>) => {
            state.coordinates[action.payload.idx] = action.payload.trace;
        },
        clear: (state) => {
            state.coordinates = [];
        },
        removeCoordinate: (state, action: PayloadAction<string>) => {
            state.coordinates = state.coordinates.filter((c) => c.id !== action.payload);
        },
        setDraggingCoordinate: (state, action: PayloadAction<State["draggingCoordinate"]>) => {
            state.draggingCoordinate = action.payload;
        },
        setHideCoordinateGizmo: (state, action: PayloadAction<State["hideCoordinateGizmo"]>) => {
            state.hideCoordinateGizmo = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(setViewpoint, (state, action) => {
            const coordinatesData = action.payload.measurements?.coordinates;
            if (!coordinatesData) {
                if (action.meta.ignoreUndefined) {
                    return;
                }
                state.coordinates = [];
                return;
            }
            state.coordinates = coordinatesData.map((c) => ({
                id: c.id,
                point: {
                    position: c.point.position,
                    guides: c.point.guides,
                    snapType: c.point.snap_type,
                },
                normal: c.normal,
                screenOffset: { x: 0, y: 0 },
            }));
        });
    },
});

export const selectCoordinates = (state: RootState) => state.coordinates.coordinates;
export const selectDraggingCoordinate = (state: RootState) => state.coordinates.draggingCoordinate;
export const selectHideCoordinateGizmo = (state: RootState) => state.coordinates.hideCoordinateGizmo;

const { actions, reducer } = coordinatesSlice;
export { actions as coordinatesActions, reducer as coordinatesReducer };
